/**
 * Typed pub/sub — THE cross-module communication channel.
 *
 * Replaces `window.dispatchEvent(new CustomEvent(...))` across the app.
 * Each event is registered in `AppEvents` with its typed payload so
 * subscribers receive the exact shape (no `any`, no runtime parsing).
 *
 * Guidelines:
 *   - Only use the bus for *cross-module* communication. Within a
 *     single module, prefer props or local state.
 *   - Name events with a kebab-case verb in past tense (`group-created`)
 *     for facts and infinitive (`open-plans`) for commands.
 *   - Keep payloads small + serializable. No React nodes, no DOM refs.
 */

/** Minimal chat summary carried in chat-created events. Bus stays free
 *  of module-specific DTOs — keep this a deliberately thin shape. */
export type BusChatSummary = {
    id: string;
    name: string;
    project_id?: string | null;
    updatedAt?: string;
};

export type ProjectSummary = {
    id: string;
    name: string;
    description?: string;
    chats: { id: string; name: string; updatedAt?: string }[];
};

export type AppEvents = {
    // ── Commands (imperative) ───────────────────────────────
    'open-plans': { reason?: 'quota' | 'paywall' | 'paywall-compare' | 'menu' } | void;
    'open-create-project-modal': { chatId?: string | null } | void;

    // ── Chat / messaging facts ──────────────────────────────
    'project-chat-created': { projectId: string; chat: BusChatSummary };
    'project-chat-touched': { projectId: string; chatId: string };
    'direct-chat-created': BusChatSummary;
    'direct-chat-touched': { chatId: string };
    'chat-moved': {
        chatId: string;
        fromProjectId: string | null;
        toProjectId: string | null;
    };
    'sidebar-projects-loaded': { projects: ProjectSummary[] } | void;

    // ── Group chats ─────────────────────────────────────────
    /** Fired after a new group chat has been persisted server-side so
     *  the inbox can refetch and surface it in the Rooms folder. */
    'group-created': { groupId: string };

    // ── User / session ──────────────────────────────────────
    /** Nudge UserProvider to refetch after events that change the
     *  server-side user shape (checkout, role grant, profile save). */
    'user-invalidate': { reason: 'checkout' | 'role-grant' | 'profile-save' | 'manual' };

    // ── Cross-module navigation ────────────────────────────
    /** Open the Messages tab and seed a DM conversation with the given
     *  professional. Used by Networking's Accept-request flow and the
     *  Mentor review list's "Message mentee" action so users don't have
     *  to manually tab-hunt after a terminal action. */
    'open-dm-with-user': { userId: string; name?: string; avatar?: string };
    /** Open the Mentorship tab focused on a specific submission — used
     *  by the mentor review list's inline Review button so reviewers
     *  don't have to tab-hop to reach the assessment view. */
    'open-submission-review': { submissionId: string; menteeId?: string };
    /** Open the Mentorship tab and seed a fresh "active task" (new
     *  submission). Used by the Education → Coaching bridge (after a
     *  mentee finishes a lesson or bootcamp week) so reflecting on
     *  what they just read is one click, not a tab hunt + scroll.
     *  The optional `originLesson*` payload lets MenteeActiveTask
     *  surface a "Reflecting on: {title}" eyebrow with a back-link. */
    'open-active-task': {
        originLessonId?: string;
        originLessonTitle?: string;
    } | void;
    /** Fired by the NotificationsBell when the user clicks a row that
     *  carries a `default_url`. App.tsx subscribes and routes to the
     *  right tab + emits the corresponding take-over bus event so the
     *  user lands directly on the relevant surface — no flash of a
     *  dashboard root before the take-over mounts. */
    'notification-clicked': { url: string; category: string };
};

type EventKey = keyof AppEvents;
type Handler<K extends EventKey> = (payload: AppEvents[K]) => void;

const listeners: { [K in EventKey]?: Set<Handler<K>> } = {};

export function emit<K extends EventKey>(event: K, payload: AppEvents[K]): void {
    const set = listeners[event];
    if (!set) return;
    for (const handler of set) {
        try {
            handler(payload);
        } catch (err) {
            // Isolate subscriber failures so one bad handler doesn't
            // break the rest of the chain.
            console.error(`[events] handler for "${event}" threw:`, err);
        }
    }
}

export function on<K extends EventKey>(event: K, handler: Handler<K>): () => void {
    let set = listeners[event] as Set<Handler<K>> | undefined;
    if (!set) {
        set = new Set<Handler<K>>();
        (listeners as Record<EventKey, Set<Handler<EventKey>>>)[event] = set as unknown as Set<Handler<EventKey>>;
    }
    set.add(handler);
    return () => {
        set!.delete(handler);
    };
}
